import { env } from './env.ts';
import { Cipher, type EncryptedPayload } from './crypto.ts';
import { KeyValueStore } from './storage.ts';
import { generateToken } from './utils.ts';
import type { DaoUser } from '@/types/dao.ts';

/** 앱에서 사용하는 세션 키 목록. 새 키는 여기 먼저 추가한다. */
export type SessionKey = 'session-data' | 'session-id' | 'session-token' | 'watched';

/** 앱 전역 세션 스토리지 */
export const session = new KeyValueStore<SessionKey>({
  prefix: 'app:',
  throwOnError: true,
});

/** 세션 데이터 난독화용 */
export const cipher = new Cipher({ secret: env.encryptKey });

/** 비로그인 사용자 식별 토큰. 없으면 만들어 저장한다. */
export function ensureClientSessionId(): string {
  const existing = session.get<string>('session-id');
  if (existing) return existing;

  const created = generateToken(32);
  session.set<string>('session-id', created);
  return created;
}

/**
 * API 로그인 토큰.
 *
 * 로그인할 때 **서버가 발급**해 준 값이다. 이후 모든 요청의
 * `Authorization: Bearer` 로 나가고, 서버는 이 토큰에서 회원을 꺼낸다.
 *
 * ★ 예전에는 요청 본문의 `userSeq` 만으로 회원을 정했다. 본문을 감싼 키가 이
 *   번들에 들어 있어서, 사실상 누구나 남의 자료를 꺼낼 수 있었다.
 *   (2026-09-11, 백엔드 `ModuleToken`)
 *
 * ★ 난독화하지 않고 그대로 둔다. 같은 브라우저 안에서의 난독화는 의미가 없고,
 *   토큰은 서버가 언제든 폐기할 수 있다.
 */
export function getSessionToken(): string | null {
  return session.get<string>('session-token') ?? null;
}

export function setSessionToken(token: string | null | undefined): void {
  if (token) session.set<string>('session-token', token);
  else session.remove('session-token');
}

/** 저장된 로그인 정보를 복호화해서 반환. 없거나 깨졌으면 null */
export async function loadStoredUser(): Promise<DaoUser | null> {
  const payload = session.get<EncryptedPayload>('session-data');
  if (!payload) return null;

  try {
    return await cipher.decryptJson<DaoUser>(payload);
  } catch {
    // 키 변경·손상 등으로 복호화 실패하면 버린다.
    session.remove('session-data');
    return null;
  }
}

/** 로그인 정보를 암호화해서 저장 */
export async function storeUser(user: DaoUser): Promise<void> {
  const payload = await cipher.encryptJson(user);
  session.set('session-data', payload);
}

/** 로그인 정보 삭제 */
export function clearStoredUser(): void {
  session.remove('session-data');
  session.remove('session-token');
}
